/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { useProgressButtonLabelLayoutTheme } from '#hooks/PRIVATE_useProgressButtonLabelLayoutTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { AdjustedProgressCircle } from './AdjustedProgressCircle';

export type Props = {
  /**
   *The default slot is the button's text label. The oj-c-button element accepts plain text or DOM nodes as children for the default slot."
   */
  children?: ComponentChildren;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * Styling can be used for customizing icon layout by limiting padding ("embedded")
   * The label can fill available width ("fill").
   */
  styling?: 'default' | 'embedded' | 'fill';

  /**
   * The size indicates how large the button is rendered.
   */
  size?: ComponentProps<typeof BaseButton>['size'];

  /**
   * Display indicates whether the label, the icons, or all elements should be rendered by the button.
   */
  display?: 'icons' | 'label' | 'all';

  /**
   * Specifies that a quick indeterminate action is taking place, and a progress circle should be rendered.
   */
  isLoading?: boolean;

  /**
   * This indicates that the Loading indicator should be rendered in contrast.
   */
  isLoadingContrast?: boolean;
};
/**
 * ProgressButtonLabelLayout provides the layout for progress button labels.
 * @param param0
 * @returns
 */
export function ProgressButtonLabelLayout({
  size = 'md',
  display = 'all',
  styling = 'default',
  isLoadingContrast = false,
  isLoading = false,
  children,
  startIcon
}: Props) {
  const {
    startIconClasses,
    labelClasses,
    labelStyles,
    progressIconClasses,
    containerClasses,
    isShowIcons,
    isShowText,
    hasTextStartIcon
  } = useProgressButtonLabelLayoutTheme({
    display,
    size,
    styling,
    isLoading,
    startIcon,
    children
  });

  const isLabelOnly = display === 'label' || !startIcon;
  const mergedContainerClasses = classNames([
    containerClasses,
    isLoading ? progressIconClasses : undefined
  ]);

  return (
    <span class={mergedContainerClasses}>
      {isShowIcons && startIcon && (
        <span class={startIconClasses} aria-hidden={true}>
          {isLoading ? <AdjustedProgressCircle isLoadingContrast={isLoadingContrast} /> : startIcon}
        </span>
      )}
      {isShowText && children && (
        <span aria-hidden={true} class={isLabelOnly ? undefined : labelClasses}>
          {' '}
          {children}{' '}
        </span>
      )}
      {isShowText && children && isLoading && !hasTextStartIcon && (
        <span class={labelStyles['floatingIcon']} aria-hidden={true}>
          <AdjustedProgressCircle isLoadingContrast={isLoadingContrast} />
        </span>
      )}
    </span>
  );
}

ProgressButtonLabelLayout.displayName = 'ProgressButtonLabelLayout';
